import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';

import useBoardStore from '../../../store/boardStore';
import useUserStore from '../../../store/userStore';

const formatChatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const now = new Date();
  const ageMs = now.getTime() - date.getTime();
  const sameDay = ageMs < 12 * 60 * 60 * 1000;

  if (sameDay) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  return date.toLocaleString([], {
    weekday: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ChatPanel = () => {
  const chat = useBoardStore((state) => state.chat);
  const boardDetails = useBoardStore((state) => state.boardDetails);
  const addChatMessage = useBoardStore((state) => state.addChatMessage);
  const currentUser = useUserStore((state) => state.getCurrentUser());
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const [draftMessage, setDraftMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const chatEnabled = boardDetails?.chatEnabled !== false;
  const isBoardLocked = boardDetails?.locked === true;
  const canSendMessage =
    Boolean(currentUser) && chatEnabled && !isBoardLocked && draftMessage.trim().length > 0;
  const orderedChat = useMemo(
    () =>
      [...chat].sort(
        (left, right) => new Date(left.created).getTime() - new Date(right.created).getTime()
      ),
    [chat]
  );

  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;

    if (!messagesContainer) {
      return;
    }

    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }, [orderedChat]);

  const sendChatMessage = async () => {
    if (!canSendMessage || isSending) {
      return;
    }

    setIsSending(true);
    await addChatMessage(draftMessage);
    setDraftMessage('');
    setIsSending(false);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: '#ffd04b',
        }}
      >
        <Typography variant="h6">Chat</Typography>
      </Box>

      <Stack
        ref={messagesContainerRef}
        spacing={1.25}
        sx={{
          px: 2,
          py: 1.5,
          maxHeight: { xs: 260, md: 340 },
          overflowY: 'auto',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {orderedChat.map((chatItem, index) => {
          const previousMessage = index > 0 ? orderedChat[index - 1] : null;
          const sameOwner = previousMessage && previousMessage.ownerName === chatItem.ownerName;
          const withinFiveMinutes =
            previousMessage &&
            Math.abs(
              new Date(chatItem.created).getTime() - new Date(previousMessage.created).getTime()
            ) <
              5 * 60 * 1000;
          const showMeta = !(sameOwner && withinFiveMinutes);

          return (
            <Box key={`${chatItem.ownerName}-${chatItem.created}-${index}`}>
              {showMeta && (
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                  <Typography variant="caption" fontWeight={700}>
                    {chatItem.ownerName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatChatDate(chatItem.created)}
                  </Typography>
                </Stack>
              )}
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {chatItem.content}
              </Typography>
            </Box>
          );
        })}
        {orderedChat.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No chat messages yet
          </Typography>
        )}
      </Stack>

      <Box sx={{ p: 1.5 }}>
        {chatEnabled ? (
          isBoardLocked ? (
            <Typography variant="body2" color="text.secondary">
              Board is locked. New chat messages are disabled.
            </Typography>
          ) : currentUser ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Write a message..."
                value={draftMessage}
                onChange={(event) => setDraftMessage(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void sendChatMessage();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={() => void sendChatMessage()}
                disabled={!canSendMessage || isSending}
              >
                Send
              </Button>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Join the board to post chat messages.
            </Typography>
          )
        ) : (
          <Typography variant="body2" color="text.secondary">
            Chat is disabled for this board.
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default ChatPanel;
